import Vue from 'vue';
import Component from 'vue-class-component';
import {AXIOS} from "@/lib/http-common";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import ExportLayout from "@/components/properties/settings/export/model/ExportLayout";
import {MeasurementFields} from "@/components/properties/settings/measure-units/model/MeasurementFields";
import {DimensionUnit} from "@/components/properties/settings/measure-units/model/DimensionUnit";
import {VolumeUnit} from "@/components/properties/settings/measure-units/model/VolumeUnit";
import {WeightUnit} from "@/components/properties/settings/measure-units/model/WeightUnit";
import {DateTimeFormats} from "@/components/properties/settings/export/model/DateTimeFormats";
import draggable from 'vuedraggable';
import {VolumeUnitRaw} from "@/components/properties/settings/measure-units/model/VolumeUnitRaw";

@Component({
  computed: {
    DateTimeFormats() {
      return DateTimeFormats;
    }
  },
  components: {
    draggable
  }
})
export default class ExportLayoutSettingsView extends Vue {
  public loading: boolean = false;
  public snack = false;
  public snackColor = '';
  public snackText = '';
  public exportLayoutFromDatabase: ExportLayout[] = [];
  public editExportFieldDialogOpened: boolean = false;
  public addExportFieldDialogOpened: boolean = false;
  public fieldToEdit = this.getInitializedField();
  public fieldToAdd = this.getInitializedField();
  public isEditFieldFormValid: any = null;
  public isAddFieldFormValid: any = null;
  public search = '';
  public sortBy = 'sequence';
  public disabledFields: ExportLayout[] = [];
  public enabledFields: ExportLayout[] = [];
  public selectedFieldName: string = '';

  get exportLayoutTableHeaders() {
    return [
      {text: this.$t("exportLayout.tableHeaders.cubiscanFieldName"), value: "cubiscanFieldName"},
      {text: this.$t("exportLayout.tableHeaders.cubiscanFieldType"), value: "cubiscanFieldType"},
      {text: this.$t("exportLayout.tableHeaders.customFieldName"), value: "customFieldName"},
      {text: this.$t("exportLayout.tableHeaders.decimals"), value: "decimals"},
      {text: this.$t("exportLayout.tableHeaders.unit"), value: "unit"},
      {text: this.$t("exportLayout.tableHeaders.dateFormat"), value: "dateFormat"},
      {text: this.$t('exportLayout.tableHeaders.action'), value: "action", sortable: false}
    ];
  }

  public mounted() {
    this.getExportLayoutFromDatabase();
  }

  public getExportLayoutFromDatabase() {
    this.loading = true;
    AXIOS.get('/export-layout/')
      .then((message) => {
        this.exportLayoutFromDatabase = message.data;

        // Separate enabled and disabled fields
        this.enabledFields = this.exportLayoutFromDatabase
          .filter(item => item.enabled)
          .sort((a, b) => a.sequence - b.sequence);

        // Ensure disabled fields have sequence = 0
        this.exportLayoutFromDatabase
          .filter(item => !item.enabled)
          .forEach(item => {
            item.sequence = 0;
          });

      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getDisabledFields() {
    this.loading = true;
    AXIOS.get('/export-layout/disabled')
      .then((message) => {
        this.disabledFields = message.data;
        this.disabledFields.forEach(value => {
          value.sequence = 0;
        });
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getDisabledFieldsNames(): string[] {
    const disabledFieldsNames: string[] = [];
    this.disabledFields.forEach(value => disabledFieldsNames.push(value.cubiscanFieldName));
    return disabledFieldsNames;
  }

  public save() {
    this.snack = true;
    this.snackColor = 'success';
    this.snackText = 'Data saved';
  }

  public cancel() {
    this.snack = true;
    this.snackColor = 'error';
    this.snackText = 'Canceled';
  }

  public openEditExportFieldDialog(field: ExportLayout) {
    this.editExportFieldDialogOpened = true;
    this.fieldToEdit.cubiscanFieldName = field.cubiscanFieldName;
    this.fieldToEdit.cubiscanFieldType = field.cubiscanFieldType;
    this.fieldToEdit.customFieldName = field.customFieldName;
    this.fieldToEdit.dateFormat = field.dateFormat;
    this.fieldToEdit.decimals = field.decimals;
    this.fieldToEdit.unit = field.unit;
    this.fieldToEdit.enabled = field.enabled;
    this.fieldToEdit.sequence = field.sequence;
  }

  public openAddExportFieldDialog() {
    this.getDisabledFields();
    this.addExportFieldDialogOpened = true;
  }

  public closeEditExportFieldDialog() {
    this.editExportFieldDialogOpened = false;
    this.resetValidationEditForm();
    this.fieldToEdit = this.getInitializedField();
    this.getExportLayoutFromDatabase();
  }

  public closeAddExportFieldDialog() {
    this.addExportFieldDialogOpened = false;
    this.resetValidationAddForm();
    this.fieldToAdd = this.getInitializedField();
    this.selectedFieldName = '';
    this.getExportLayoutFromDatabase();
  }

  public resetValidationEditForm() {
    (this.$refs.editExportField as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public resetValidationAddForm() {
    (this.$refs.addExportField as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public getInitializedField(): ExportLayout {
    return {
      cubiscanFieldName: "",
      cubiscanFieldType: "",
      customFieldName: "",
      dateFormat: "",
      decimals: 0,
      enabled: true,
      unit: "",
      sequence: 0
    };
  }

  public editField() {
    this.updateField(this.fieldToEdit);
    this.closeEditExportFieldDialog();
  }

  public addField() {
    this.fieldToAdd.enabled = true;
    this.updateField(this.fieldToAdd);
    this.closeAddExportFieldDialog();
  }

  public updateField(field: ExportLayout) {
    this.loading = true;
    AXIOS.put('/export-layout/', field)
      .then(() => {
        this.getExportLayoutFromDatabase();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public updateFieldList(fields: ExportLayout[]) {
    this.loading = true;
    AXIOS.put('/export-layout/update', fields)
      .then(() => {
        UserActionResultSender.showSuccessFeedback("exportLayout.update.success");
        this.getExportLayoutFromDatabase();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }


  public isMeasurementField(field: ExportLayout): boolean {
    return Object.values(MeasurementFields).includes(field.cubiscanFieldName as MeasurementFields);
  }

  public isDateField(field: ExportLayout): boolean {
    return ["gendate", "moddate"].includes(field.cubiscanFieldName);
  }

  public getAvailableUnits(fieldName: string): any[] {
    if ([MeasurementFields.LENGTH.valueOf(), MeasurementFields.WIDTH.valueOf(), MeasurementFields.HEIGHT.valueOf()].includes(fieldName)) {
      return Object.values(DimensionUnit);
    } else if (fieldName === MeasurementFields.VOLUME) {
      return [...Object.values(VolumeUnit), ...Object.values(VolumeUnitRaw)];
    } else if (fieldName === MeasurementFields.WEIGHT) {
      return Object.values(WeightUnit);
    } else {
      return [];
    }
  }

  public enableAll() {
    this.exportLayoutFromDatabase.forEach(value => value.enabled = true);
    this.updateFieldList(this.exportLayoutFromDatabase);
  }

  public disableExportField(field: ExportLayout) {
    field.enabled = false;
    this.updateField(field);
  }

  public disableAll() {
    this.exportLayoutFromDatabase.forEach(value => value.enabled = false);
    this.updateFieldList(this.exportLayoutFromDatabase);
    this.fieldToAdd = this.getInitializedField();
    this.fieldToEdit = this.getInitializedField();
  }

  public setSelectedField(event: string) {
    const field = this.disabledFields.find(value => value.cubiscanFieldName === event);
    if (field) {
      this.fieldToAdd = field;
    }
  }

  public updateSequence() {
    // Update the sequence numbers based on the current order
    this.enabledFields.forEach((item, index) => {
      item.sequence = index + 1;
    });

    // Update all sequences in a single API call
    this.updateFieldSequences(this.enabledFields);
  }

  public updateFieldSequences(fields: ExportLayout[]) {
    this.loading = true;
    AXIOS.put('/export-layout/sequences', fields)
      .then(() => {
        UserActionResultSender.showSuccessFeedback("exportLayout.update.success");
        this.getExportLayoutFromDatabase();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public onDragEnd() {
    this.updateSequence();
  }


}
